/** Canvas Surface + Workspace reveal backdrop — the actual moving layer
 * (VISTOERA_WORKSPACE_CANVAS_BIDIRECTIONAL_SPATIAL_SHELL_TRANSITION_AND_VIEWPORT_CONTINUITY_P0_V1
 * §6/§7/§52/§53).
 *
 * `CanvasTransitionSurface` wraps `.pcv-fullscreen-shell` (already the
 * Canvas route's ONE ancestor per `WorkspaceLayout.tsx` — Topbar, React
 * Flow, Nodes, Edges, MiniMap, Controls, overlays all live inside it) —
 * so the whole thing moves as ONE compositor layer via `transform:
 * translate3d(...)` (CSS in `production-canvas.css`), never touching
 * width/left/margin/padding or any individual node/edge.
 *
 * `WorkspaceRevealBackdrop` is a purely decorative, static (no state, no
 * effects, no API calls) stand-in for "Workspace is right behind Canvas"
 * during the Exit slide (spec §13: "如果真实 Workspace 尚未 mount，可以使用
 * route transition background / destination shell完成视觉连续性"). It is
 * NOT the real Workspace shell — mounting the actual Sider/Menu/Overview
 * tree just for a ~260ms transition would violate spec §9/§10's "不要长期
 * 双挂载" intent even bounded to the transition window, and buys nothing
 * visually a few static color blocks don't already deliver. */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import {
  canvasTransitionDurationMs,
  isEnterFromWorkspaceState,
  useCanvasWorkspaceTransition,
} from './canvasWorkspaceTransition'

type EnterPhase = 'start' | 'active' | 'done'

export function CanvasTransitionSurface({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { phase: exitPhase } = useCanvasWorkspaceTransition()

  // Evaluated ONCE at mount (this component only (re)mounts when
  // `WorkspaceLayout`'s branch actually swaps Workspace→Canvas — a
  // Canvas→Canvas project switch, e.g. `CanvasTopBar`'s recent-project
  // popover, keeps this same mounted instance and never re-triggers the
  // Enter slide, which is exactly the desired behavior).
  const [enterPhase, setEnterPhase] = useState<EnterPhase>(() =>
    isEnterFromWorkspaceState(location.state) ? 'start' : 'done',
  )
  const rafRef = useRef<number | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useLayoutEffect(() => {
    // Mount-once by design — see the `enterPhase` initializer above.
    if (enterPhase !== 'start') return
    const duration = canvasTransitionDurationMs()
    if (duration <= 0) {
      setEnterPhase('done')
      return
    }
    // rAF so the FIRST painted frame is still off-screen right (spec
    // §16/§17) before the class swap that triggers the CSS transition.
    rafRef.current = requestAnimationFrame(() => setEnterPhase('active'))
    timerRef.current = setTimeout(() => setEnterPhase('done'), duration + 40)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      if (timerRef.current != null) clearTimeout(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const className = [
    'pcv-fullscreen-shell',
    'pcv-canvas-transition-surface',
    exitPhase === 'exiting' ? 'is-exiting' : '',
    enterPhase === 'start' ? 'is-enter-start' : '',
    enterPhase === 'active' ? 'is-enter-active' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return <div className={className}>{children}</div>
}

export function WorkspaceRevealBackdrop() {
  return (
    <div className="pcv-workspace-reveal-backdrop" aria-hidden="true">
      <div className="pcv-workspace-reveal-backdrop-sider" />
      <div className="pcv-workspace-reveal-backdrop-header" />
    </div>
  )
}
